import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Plus, Sparkles, TrendingUp, MoreVertical, Edit2, Trash2, Settings } from 'lucide-react';
import Sidebar from '../components/layout/Sidebar';
import { useSidebar } from '../contexts/SidebarContext';
import { useAuth } from '../contexts/AuthContext';
import chatService from '../services/chatService';
import FocusModal from '../components/modals/FocusModal';
import DeleteFocusModal from '../components/modals/DeleteFocusModal';
import ChangeFocusImageModal from '../components/modals/ChangeFocusImageModal';

// Background gradient styles for focuses
const gradientStyles = [
  'from-slate-100 to-slate-200',
  'from-stone-100 to-stone-200',
  'from-zinc-100 to-zinc-200',
  'from-gray-100 to-gray-200',
  'from-neutral-100 to-neutral-200',
  'from-blue-50 to-blue-100',
  'from-purple-50 to-purple-100',
  'from-pink-50 to-pink-100',
];

// FocusCard Component with Edit/Delete/Change Image actions
function FocusCard({ focus, onClick, onEdit, onDelete, onChangeImage }) {
  const [showMenu, setShowMenu] = useState(false);
  
  // Get a consistent gradient style based on focus ID
  const gradientStyle = gradientStyles[parseInt(focus.id.slice(0, 8), 16) % gradientStyles.length];

  const handleEdit = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onEdit(focus);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onDelete(focus);
  };
  
  const handleChangeImage = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onChangeImage(focus);
  };

  return (
    <div
      className="group relative h-72 rounded-2xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.01] hover:shadow-xl shadow-sm bg-white border border-gray-200/80"
    >
      {/* Cover image or gradient background */}
      {focus.cover_image_url ? (
        <>
          <img 
            src={focus.cover_image_url} 
            alt={focus.title}
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        </>
      ) : (
        <>
      {/* Subtle background gradient */}
          <div className={`absolute inset-0 bg-gradient-to-br ${gradientStyle} opacity-40`} />
      
      {/* Soft texture overlay */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }} />
        </>
      )}
      
      {/* Hover border */}
      <div className="absolute inset-0 border border-gray-300/0 group-hover:border-gray-300/50 rounded-2xl transition-all duration-300" />
      
      {/* Content with generous padding */}
      <div 
        className="relative h-full p-8 flex flex-col justify-between"
        onClick={onClick}
      >
        {/* Top section */}
        <div className="flex items-start justify-between gap-6">
          <h3 className={`text-xl font-medium leading-relaxed tracking-tight ${
            focus.cover_image_url ? 'text-white' : 'text-gray-900'
          }`}>
            {focus.title}
          </h3>
          
          <div className="flex items-center gap-2">
            {/* Menu button */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowMenu(!showMenu);
                }}
                className={`p-1.5 rounded-lg transition-all ${
                  focus.cover_image_url
                    ? 'hover:bg-white/20 text-white'
                    : 'hover:bg-gray-100 text-gray-600'
                } ${showMenu ? 'bg-gray-100' : ''}`}
              >
                <MoreVertical size={16} />
              </button>
              
              {/* Dropdown menu */}
              {showMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowMenu(false);
                    }}
                  />
                  <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20">
                    <button
                      onClick={handleEdit}
                      className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-left"
                    >
                      <Edit2 size={14} />
                      Edit Details
                    </button>
                    <button
                      onClick={handleChangeImage}
                      className="w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-left"
                    >
                      <Settings size={14} />
                      Change Image
                    </button>
                    <button
                      onClick={handleDelete}
                      className="w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 text-left border-t border-gray-100"
                    >
                      <Trash2 size={14} />
                      Delete Focus
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
        
        {/* Bottom section - show description if available */}
        {focus.description && (
          <div className={`${focus.cover_image_url ? 'text-white/90' : 'text-gray-600'} text-sm leading-relaxed line-clamp-2`}>
            {focus.description}
          </div>
        )}
      </div>
    </div>
  );
}

// EmptyState Component
function EmptyState({ onCreateFirst }) {
  return (
    <div className="flex flex-col items-center justify-center py-32 px-4">
      <div className="w-20 h-20 rounded-2xl bg-gray-100 flex items-center justify-center mb-8 border border-gray-200">
        <TrendingUp size={32} className="text-gray-400" />
      </div>
      <h3 className="text-xl font-medium text-gray-900 mb-4">
        You haven't created any Focus yet
      </h3>
      <p className="text-base text-gray-600 text-center max-w-md mb-10 leading-relaxed">
        Pick a topic that matters to you and let Swirl think about it with you.
      </p>
      <button
        onClick={onCreateFirst}
        className="px-6 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2"
      >
        <Plus size={18} />
        Create your first Focus
      </button>
    </div>
  );
}

// Main FocusPage Component
export default function FocusPage() {
  const { isExpanded, toggle } = useSidebar();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [focuses, setFocuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Modal states
  const [showFocusModal, setShowFocusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentFocus, setCurrentFocus] = useState(null);
  const [modalMode, setModalMode] = useState('create');

  // Load focuses and suggestions on mount
  useEffect(() => {
    if (user) {
      loadFocuses();
      loadSuggestions();
    }
  }, [user]);

  const loadFocuses = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: err } = await chatService.getUserFocuses(user.id);
      
      if (err) {
        // Extract error message from Supabase error object
        const errorMessage = typeof err === 'string' ? err : err.message || err.error_description || JSON.stringify(err);
        throw new Error(errorMessage);
      }
      
      // All focuses are now "active" by default, no filtering needed
      setFocuses(data || []);
    } catch (err) {
      console.error('Error loading focuses:', err);
      const errorMessage = err.message || (typeof err === 'string' ? err : 'Failed to load focuses');
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      setLoadingSuggestions(true);
      
      // Simple default suggestions based on common use cases
      setSuggestions([
        {
          title: 'Personal Growth',
          description: 'Track your self-improvement journey, learning goals, and habits',
          mission: 'Become the best version of yourself'
        },
        {
          title: 'Creative Projects',
          description: 'Organize your creative ideas, writing, art, and inspiration',
          mission: 'Bring your creative visions to life'
        },
        {
          title: 'Career Development',
          description: 'Manage your professional goals, skills, and opportunities',
          mission: 'Advance your career and build expertise'
        }
      ]);
    } catch (err) {
      console.error('Error loading suggestions:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  const handleFocusClick = (focusId) => {
    navigate(`/focus/${focusId}`);
  };

  const handleNewFocus = () => {
    setCurrentFocus(null);
    setModalMode('create');
    setShowFocusModal(true);
  };

  const handleCreateFromSuggestion = (suggestion) => {
    setCurrentFocus(suggestion);
    setModalMode('create');
    setShowFocusModal(true);
  };

  const handleEditFocus = (focus) => {
    setCurrentFocus(focus);
    setModalMode('edit');
    setShowFocusModal(true);
  };

  const handleDeleteFocus = (focus) => {
    setCurrentFocus(focus);
    setShowDeleteModal(true);
  };
  
  const handleChangeImage = (focus) => {
    setCurrentFocus(focus);
    setShowImageModal(true);
  };

  const handleSaveFocus = async (focusData) => {
    try {
      if (modalMode === 'create') {
        // Create new focus
        const { data, error } = await chatService.createFocus(user.id, focusData);
        if (error) throw new Error(error);
        
        // Add to list
        setFocuses(prev => [data, ...prev]);
      } else {
        // Update existing focus
        const { data, error } = await chatService.updateFocus(user.id, currentFocus.id, focusData);
        if (error) throw new Error(error);
        
        // Update in list
        setFocuses(prev => prev.map(f => f.id === currentFocus.id ? data : f));
      }
    } catch (err) {
      console.error('Error saving focus:', err);
      throw err;
    }
  };

  const handleConfirmDelete = async (focusId) => {
    try {
      const { error } = await chatService.deleteFocus(user.id, focusId);
      if (error) throw new Error(error);
      
      // Remove from list
      setFocuses(prev => prev.filter(f => f.id !== focusId));
    } catch (err) {
      console.error('Error deleting focus:', err);
      throw err;
    }
  };

  return (
    <div className="flex h-screen" style={{ backgroundColor: '#F7F7F8' }}>
      <Sidebar
        isExpanded={isExpanded}
        onToggle={toggle}
      />
      
      {/* Arrow for minimized sidebar */}
      {!isExpanded && (
        <button
          onClick={toggle}
          className="fixed left-16 top-8 w-6 h-6 bg-white/40 backdrop-blur-lg rounded-r-lg flex items-center justify-center text-gray-800 hover:bg-white/50 transition-all duration-500 ease-out z-[60] shadow-lg border border-white/30"
        >
          <ChevronRight size={16} />
        </button>
      )}
      
      <main className={`flex-1 overflow-y-auto transition-all duration-500 ease-out ${isExpanded ? 'ml-72' : 'ml-16'} flex flex-col`}>
        {/* Header Section */}
        <div className="relative z-10 px-12 pt-16 pb-12">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-start justify-between">
              <div className="space-y-3">
                <h1 className="text-4xl font-medium text-gray-900 tracking-tight">
                  Focus
          </h1>
                <p className="text-[17px] text-gray-600 leading-relaxed max-w-2xl">
            Workspaces that bring your saved ideas, tools, and chats into one place.
          </p>
              </div>
              
              {/* New Focus Button */}
              <button
                onClick={handleNewFocus}
                className="px-5 py-2.5 bg-gray-900 hover:bg-gray-800 text-white rounded-lg font-medium text-sm transition-all duration-200 flex items-center gap-2"
              >
                <Plus size={18} />
                New Focus
              </button>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative z-10 px-12 space-y-24 pb-24">
          <div className="max-w-7xl mx-auto">
            {/* Loading State */}
            {loading && (
              <div className="flex items-center justify-center py-32">
                <div className="text-center">
                  <div className="w-12 h-12 border-4 border-gray-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading your focuses...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && !loading && (
              <div className="p-6 bg-red-50 border border-red-200 rounded-xl max-w-2xl mx-auto">
                <p className="text-red-600">Error loading focuses: {error}</p>
                <button
                  onClick={loadFocuses}
                  className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* Your Focuses Section */}
            {!loading && !error && (
              <>
            <section>
              {focuses.length === 0 ? (
                <EmptyState onCreateFirst={handleNewFocus} />
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {focuses.map(focus => (
                    <FocusCard
                      key={focus.id}
                      focus={focus}
                      onClick={() => handleFocusClick(focus.id)}
                          onEdit={handleEditFocus}
                          onDelete={handleDeleteFocus}
                          onChangeImage={handleChangeImage}
                    />
                  ))}
                </div>
              )}
            </section>

            {/* Suggested by Swirl Section */}
                {suggestions.length > 0 && (
                  <section className="mt-24">
                    <div className="flex items-center gap-2.5 mb-3">
                      <Sparkles size={22} className="text-purple-600" />
                      <h2 className="text-2xl font-semibold text-gray-900 tracking-tight">Suggested by Swirl</h2>
                </div>
                    <p className="text-[15px] text-gray-500 mb-8">Based on what you have been saving and asking.</p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      {suggestions.map((suggestion, index) => {
                        const gradients = [
                          'from-purple-400 to-pink-400',
                          'from-blue-400 to-cyan-400',
                          'from-orange-400 to-yellow-400',
                          'from-emerald-400 to-teal-400'
                        ];
                        const gradient = gradients[index % gradients.length];
                        
                        return (
                          <div
                            key={index}
                            className="group relative bg-white/60 backdrop-blur-lg rounded-2xl p-6 border border-white/50 shadow-lg hover:shadow-2xl transition-all duration-200 hover:scale-[1.01] hover:-translate-y-1 h-full flex flex-col cursor-pointer"
                            onClick={() => handleCreateFromSuggestion(suggestion)}
                          >
                            {/* Gradient Border Effect on Hover */}
                            <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-200`}></div>
                            
                            <div className="relative flex-1 flex flex-col">
                              {/* Category Label */}
                              <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                                SUGGESTED
                              </div>

                              {/* Title */}
                              <h3 className="text-lg font-bold text-gray-900 mb-3">
                                {suggestion.title}
                              </h3>

                              {/* Description */}
                              <p className="text-sm text-gray-600 mb-5 leading-relaxed flex-1">
                                {suggestion.description}
                              </p>

                              {/* Action Button */}
                              <button 
                                className="w-full py-2.5 px-4 bg-white hover:bg-purple-50 text-gray-900 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center justify-center gap-2 border border-gray-200 hover:border-purple-200"
                                style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}
                              >
                                <Plus size={16} className="text-purple-600" />
                                <span>Add To Focus</span>
                              </button>
                  </div>
                          </div>
                        );
                      })}
                </div>
              </section>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      
      {/* Modals */}
      <FocusModal
        isOpen={showFocusModal}
        onClose={() => setShowFocusModal(false)}
        onSave={handleSaveFocus}
        focus={currentFocus}
        mode={modalMode}
      />
      
      <DeleteFocusModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        focus={currentFocus}
      />
      
      <ChangeFocusImageModal
        isOpen={showImageModal}
        onClose={() => {
          setShowImageModal(false);
          setCurrentFocus(null);
        }}
        focus={currentFocus}
        onImageChanged={loadFocuses}
      />
      
      <style jsx>{`
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
}
